import type { GetPatientResponse } from '../../../../services/patientCard';
import { UNSET_MANUFACTURER, hasMedicalImages, hasStls } from './submissionProgress';
import type { SubmissionFiles } from './submissionFiles';
import type { SubmissionFileAccess } from './useSubmissionFileAccess';
import { approachLabel, caseTypeLabel, manufacturerLabel } from './treatmentOptions';
import { MedicalImagesSection, StlsSection } from './RecordsSections';
import { ReadOnlyInput } from './SubmissionStepParts';

interface TreatmentStepProps {
    card: GetPatientResponse;
    files: SubmissionFiles;
    access: SubmissionFileAccess;
}

/**
 * The practice's treatment choices (mechanics, approach, manufacturer) and scan ID on one
 * row with the additional information below, followed by the dashboard's
 * medical images and STL sections.
 */
export function TreatmentStep({ card, files, access }: TreatmentStepProps) {
    const t = card.treatment;
    const manufacturer = t?.manufacturer === UNSET_MANUFACTURER ? null : t?.manufacturer;

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    <ReadOnlyInput label="Selected Mechanics" value={caseTypeLabel(t?.caseType)} />
                    <ReadOnlyInput
                        label="Selected Approach"
                        value={approachLabel(t?.subCaseType)}
                    />
                    <ReadOnlyInput label="Manufacturer" value={manufacturerLabel(manufacturer)} />
                    <ReadOnlyInput
                        label="Scan ID"
                        value={card.monitoring?.iteroScanId}
                        required={false}
                    />
                </div>
                <ReadOnlyInput label="Additional Information" value={t?.additionalInfo} multiline />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <MedicalImagesSection
                    files={files}
                    access={access}
                    complete={hasMedicalImages(files)}
                />
                <StlsSection files={files} access={access} complete={hasStls(card, files)} />
            </div>
        </div>
    );
}
